// data/feats.ts — the Ledger (DESIGN §7.3).
// DATA ONLY. `track` is a registered counter key: game code and zone mechanics
// call ctx.featTick(track, n) and game/meta.ts routes the tick to every feat
// listening on that track. Feats are the Crest economy; progress ticks during
// losing runs, and a hidden feat reveals at 50% already half-full (Fated List).
//
// Tracks that already exist in the spine: 'vaultOpened' (rooms.ts),
// 'roomsCleared', 'generatorsDown', 'zonesCleared', 'relicsBought'.
// Tracks reserved for zones not yet built are named here on purpose — the
// Provisioner's deed gates point at these feat ids and must resolve today.

import type { FeatDef } from '../types'

export const FEATS: FeatDef[] = [
  // ------------------------------------------------- first reach ×6 (1 Crest)
  {
    id: 'reach_z1',
    name: 'First Descent',
    desc: 'Set foot in the Broken Gate.',
    target: 1,
    crests: 1,
    track: 'reachZone1',
  },
  {
    id: 'reach_z2',
    name: 'Down to the Water',
    desc: 'Reach the Drowned Roots.',
    target: 1,
    crests: 1,
    track: 'reachZone2',
  },
  {
    id: 'reach_z3',
    name: 'Court Business',
    desc: 'Reach the Pale Court.',
    target: 1,
    crests: 1,
    track: 'reachZone3',
  },
  {
    id: 'reach_z4',
    name: 'Lamp in Hand',
    desc: 'Reach the Gloaming Mines.',
    target: 1,
    crests: 1,
    track: 'reachZone4',
  },
  {
    id: 'reach_z5',
    name: 'Where the Gold Went',
    desc: 'Reach the Gilded Vault.',
    target: 1,
    crests: 1,
    track: 'reachZone5',
  },
  {
    id: 'reach_z6',
    name: 'The Heart of It',
    desc: 'Reach the Gloomheart.',
    target: 1,
    crests: 1,
    track: 'reachZone6',
  },

  // -------------------------------------------- first kill ×6 (2 Crests each)
  {
    id: 'boss1_kill',
    name: 'Broodgate Broken',
    desc: 'Put down the Broodgate.',
    target: 1,
    crests: 2,
    track: 'boss1_kill',
  },
  {
    id: 'boss2_kill',
    name: 'The King Drowns',
    desc: 'Put down the Cistern King.',
    target: 1,
    crests: 2,
    track: 'boss2_kill',
  },
  {
    id: 'boss3_kill',
    name: 'Crown Sealed',
    desc: 'Put down the Twice-Crowned.',
    target: 1,
    crests: 2,
    track: 'boss3_kill',
  },
  {
    id: 'boss4_kill',
    name: 'Lamps Out',
    desc: 'Put down the Mother of Lamps.',
    target: 1,
    crests: 2,
    track: 'boss4_kill',
  },
  {
    id: 'boss5_kill',
    name: 'Starved',
    desc: 'Put down the Hoard.',
    target: 1,
    crests: 2,
    track: 'boss5_kill',
  },
  {
    id: 'boss6_kill',
    name: 'The Deep Will Remember',
    desc: 'Put down the Gloom Sovereign.',
    target: 1,
    crests: 2,
    track: 'boss6_kill',
  },

  // ------------------------------- no-hit variants ×6 (2 further Crests each)
  // DESIGN §7.3 pays a first boss kill double when it is taken clean. Modelled
  // as a separate feat on its own track so partial mastery still banks the
  // ordinary kill: clean first kill = 2 + 2 = 4 Crests.
  {
    id: 'boss1_nohit',
    name: 'Untouched at the Gate',
    desc: 'Defeat the Broodgate without being hit.',
    target: 1,
    crests: 2,
    track: 'boss1_nohit',
  },
  {
    id: 'boss2_nohit',
    name: 'Dry Feet',
    desc: 'Defeat the Cistern King without being caught by the flood.',
    target: 1,
    crests: 2,
    track: 'boss2_nohit',
  },
  {
    id: 'boss3_nohit',
    name: 'No Blood for the Court',
    desc: 'Defeat the Twice-Crowned without being hit.',
    target: 1,
    crests: 2,
    track: 'boss3_nohit',
  },
  {
    id: 'boss4_nohit',
    name: 'Steady Light',
    desc: 'Defeat the Mother of Lamps without being hit.',
    target: 1,
    crests: 2,
    track: 'boss4_nohit',
  },
  {
    id: 'boss5_nohit',
    name: 'Nothing for the Coils',
    desc: 'Defeat the Hoard without being hit.',
    target: 1,
    crests: 2,
    track: 'boss5_nohit',
  },
  {
    id: 'boss6_nohit',
    name: 'Perfect Descent',
    desc: 'Defeat the Gloom Sovereign without being hit.',
    target: 1,
    crests: 2,
    track: 'boss6_nohit',
  },

  // ------------------------------------------------------------- deed feats
  {
    id: 'vaults_four',
    name: 'Under the Clock',
    desc: 'Open four timed vaults, across any number of runs.',
    target: 4,
    crests: 2,
    track: 'vaultOpened',
  },
  {
    id: 'husks_sealed',
    name: 'Sexton',
    desc: 'Seal sixty husks in the Pale Court.',
    target: 60,
    crests: 3,
    hidden: true,
    track: 'huskSealed',
  },
  {
    id: 'unlit_banished',
    name: 'It Walks Anyway',
    desc: 'Banish the Unlit three times.',
    target: 3,
    crests: 2,
    hidden: true,
    track: 'unlitBanished',
  },
  {
    id: 'z5_frugal',
    name: 'Appraiser',
    // Tolls are excluded — they are rent, not choices (see ZONE5_RULES.frugal).
    desc: 'Clear the Gilded Vault buying under 100 gold of goods.',
    target: 1,
    crests: 3,
    hidden: true,
    track: 'z5Frugal',
  },
  {
    id: 'run_under_28',
    name: 'Straight Down',
    desc: 'Complete a full descent in under 28 minutes.',
    target: 1,
    crests: 4,
    hidden: true,
    track: 'runUnder28',
  },

  // ---------------------------------------- zone 1 family kill milestones ×3
  {
    id: 'kills_gnawling',
    name: 'Vermin Control',
    desc: 'Kill 150 Gnawlings.',
    target: 150,
    crests: 1,
    track: 'kill:gnawling',
  },
  {
    id: 'kills_husk_soldier',
    name: 'Garrison Duty',
    desc: 'Kill 80 Husk Soldiers.',
    target: 80,
    crests: 1,
    track: 'kill:husk_soldier',
  },
  {
    id: 'kills_bonecaster',
    name: 'Counter-Battery',
    desc: 'Kill 40 Bonecasters.',
    target: 40,
    crests: 2,
    track: 'kill:bonecaster',
  },
]

// --------------------------------------------------- trivial table builders

export const FEAT_BY_ID: Map<string, FeatDef> = new Map(FEATS.map((f) => [f.id, f]))

/** track -> feats listening on it (several feats may share one counter). */
export const FEATS_BY_TRACK: Map<string, FeatDef[]> = FEATS.reduce((m, f) => {
  const list = m.get(f.track)
  if (list) list.push(f)
  else m.set(f.track, [f])
  return m
}, new Map<string, FeatDef[]>())
